import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const RHVacaciones = () => {
  const [filterStatus, setFilterStatus] = useState('Todas');

  // Datos de ejemplo de vacaciones
  const solicitudes = [
    {
      id: 1,
      empleado: "Juan Pérez García",
      puesto: "Gerente de Operaciones",
      fechaInicio: "2026-02-10",
      fechaFin: "2026-02-17",
      diasSolicitados: 6,
      diasDisponibles: 12,
      motivo: "Vacaciones familiares",
      status: "Aprobada",
      fechaSolicitud: "2026-01-15",
      aprobadoPor: "Director General"
    },
    {
      id: 2,
      empleado: "María López Hernández",
      puesto: "Supervisora de Almacén",
      fechaInicio: "2026-03-05",
      fechaFin: "2026-03-12",
      diasSolicitados: 6,
      diasDisponibles: 10,
      motivo: "Viaje personal",
      status: "Pendiente",
      fechaSolicitud: "2026-01-28",
      aprobadoPor: null
    },
    {
      id: 3,
      empleado: "Carlos Ramírez Soto",
      puesto: "Mecánico A",
      fechaInicio: "2026-02-01",
      fechaFin: "2026-02-04",
      diasSolicitados: 3,
      diasDisponibles: 8,
      motivo: "Asuntos personales",
      status: "Rechazada",
      fechaSolicitud: "2026-01-25",
      aprobadoPor: "Gerente de Mantenimiento",
      motivoRechazo: "Personal insuficiente en ese periodo"
    },
    {
      id: 4,
      empleado: "Ana Martínez Torres",
      puesto: "Analista de RRHH",
      fechaInicio: "2026-04-15",
      fechaFin: "2026-04-22",
      diasSolicitados: 6,
      diasDisponibles: 14,
      motivo: "Vacaciones de Semana Santa",
      status: "Aprobada",
      fechaSolicitud: "2026-01-20",
      aprobadoPor: "Gerente de RRHH"
    },
    {
      id: 5,
      empleado: "Roberto González Cruz",
      puesto: "Operador de Tractocamión",
      fechaInicio: "2026-03-20",
      fechaFin: "2026-03-27",
      diasSolicitados: 6,
      diasDisponibles: 6,
      motivo: "Viaje familiar",
      status: "Pendiente",
      fechaSolicitud: "2026-01-29",
      aprobadoPor: null
    },
    {
      id: 6,
      empleado: "Laura Sánchez Díaz",
      puesto: "Asistente Administrativa",
      fechaInicio: "2026-02-03",
      fechaFin: "2026-02-10",
      diasSolicitados: 6,
      diasDisponibles: 12,
      motivo: "Descanso",
      status: "En curso",
      fechaSolicitud: "2026-01-10",
      aprobadoPor: "Gerente Administrativo"
    }
  ];

  const filteredSolicitudes = filterStatus === 'Todas'
    ? solicitudes
    : solicitudes.filter(s => s.status === filterStatus);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Aprobada':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'En curso':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'Pendiente':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'Rechazada':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Aprobada':
        return <CheckCircle className="w-5 h-5" />;
      case 'En curso':
        return <Clock className="w-5 h-5" />;
      case 'Pendiente':
        return <AlertCircle className="w-5 h-5" />;
      case 'Rechazada':
        return <XCircle className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const calcularDiasLaborables = (fechaInicio, fechaFin) => {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    let dias = 0;
    while (inicio <= fin) {
      if (inicio.getDay() !== 0 && inicio.getDay() !== 6) {
        dias++;
      }
      inicio.setDate(inicio.getDate() + 1);
    }
    return dias;
  };

  const totalAprobadas = solicitudes.filter(s => s.status === 'Aprobada').length;
  const totalPendientes = solicitudes.filter(s => s.status === 'Pendiente').length;
  const totalEnCurso = solicitudes.filter(s => s.status === 'En curso').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/recursos-humanos">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition"
              >
                <ArrowLeft className="w-6 h-6 text-gray-700" />
              </motion.button>
            </Link>
            <h1 className="text-4xl font-bold text-gray-800">🏖️ Control de Vacaciones</h1>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 bg-pink-500 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-pink-600 transition"
          >
            <Calendar className="w-5 h-5" />
            Nueva Solicitud
          </motion.button>
        </div>

        {/* Resumen de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Total Solicitudes</p>
              <Calendar className="w-6 h-6 text-purple-500" />
            </div>
            <p className="text-4xl font-bold text-gray-800">{solicitudes.length}</p>
            <p className="text-sm text-gray-500 mt-2">Este mes</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Aprobadas</p>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-4xl font-bold text-green-600">{totalAprobadas}</p>
            <p className="text-sm text-gray-500 mt-2">{((totalAprobadas/solicitudes.length)*100).toFixed(0)}% del total</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Pendientes</p>
              <AlertCircle className="w-6 h-6 text-orange-500" />
            </div>
            <p className="text-4xl font-bold text-orange-600">{totalPendientes}</p>
            <p className="text-sm text-gray-500 mt-2">Requieren aprobación</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">En Curso</p>
              <Clock className="w-6 h-6 text-blue-500" />
            </div>
            <p className="text-4xl font-bold text-blue-600">{totalEnCurso}</p>
            <p className="text-sm text-gray-500 mt-2">Empleados de vacaciones</p>
          </motion.div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex gap-3 flex-wrap">
            {['Todas', 'Pendiente', 'Aprobada', 'En curso', 'Rechazada'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold transition ${
                  filterStatus === status
                    ? 'bg-pink-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de solicitudes */}
        <div className="space-y-6">
          {filteredSolicitudes.map((solicitud, index) => (
            <motion.div
              key={solicitud.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {solicitud.empleado.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{solicitud.empleado}</h3>
                    <p className="text-gray-500 text-sm">{solicitud.puesto}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Solicitado: {new Date(solicitud.fechaSolicitud).toLocaleDateString('es-MX')}
                    </p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getStatusColor(solicitud.status)}`}>
                  {getStatusIcon(solicitud.status)}
                  <span className="font-semibold">{solicitud.status}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Periodo de vacaciones</p>
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Calendar className="w-4 h-4 text-pink-500" />
                    <span>{new Date(solicitud.fechaInicio).toLocaleDateString('es-MX')}</span>
                  </div>
                  <p className="text-xs text-gray-400 text-center my-1">hasta</p>
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Calendar className="w-4 h-4 text-pink-500" />
                    <span>{new Date(solicitud.fechaFin).toLocaleDateString('es-MX')}</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-2">Días</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-pink-600">{solicitud.diasSolicitados}</span>
                    <span className="text-sm text-gray-600">solicitados</span>
                  </div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-xl font-semibold text-gray-700">{solicitud.diasDisponibles}</span>
                    <span className="text-xs text-gray-500">disponibles</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-2">Motivo</p>
                  <p className="text-sm text-gray-700 font-medium">{solicitud.motivo}</p>
                  {solicitud.aprobadoPor && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500">Aprobado por:</p>
                      <p className="text-sm font-semibold text-gray-700">{solicitud.aprobadoPor}</p>
                    </div>
                  )}
                  {solicitud.motivoRechazo && (
                    <div className="mt-3 pt-3 border-t border-red-200">
                      <p className="text-xs text-red-500">Motivo de rechazo:</p>
                      <p className="text-sm font-semibold text-red-700">{solicitud.motivoRechazo}</p>
                    </div>
                  )}
                </div>
              </div>

              {solicitud.status === 'Pendiente' && (
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition font-semibold flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Aprobar
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition font-semibold flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Rechazar
                  </motion.button>
                </div>
              )}

              {solicitud.status !== 'Pendiente' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition font-semibold"
                >
                  Ver Detalles Completos
                </motion.button>
              )}
            </motion.div>
          ))}
        </div>

        {filteredSolicitudes.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">No hay solicitudes con el estado seleccionado.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RHVacaciones;
